import { type PropsWithChildren } from "react";

import {
  cn,
  type CriteriaFilterValue,
  V2Disclosure,
  V2DisclosureHeader,
  V2DisclosurePanel,
} from "@stacklet/ui";

import { FilterChip } from "./FilterChip";

interface Props extends PropsWithChildren {
  /** Title of the filter criteria section */
  title: string;
  /** Helper text shown when section is collapsed */
  helperTextOptions?: string[];
  /** Criteria options that are currently selected */
  selectedCriteria?: CriteriaFilterValue[];
  /** Callback function to remove a criterion */
  onRemoveCriterion?: (criterion: CriteriaFilterValue) => void;
}
export function FilterCriteriaCard({
  helperTextOptions,
  title,
  selectedCriteria,
  children,
  onRemoveCriterion,
}: Props) {
  return (
    <V2Disclosure
      className="rounded-xs border border-overlay-medium"
      defaultExpanded
    >
      <V2DisclosureHeader className="p-md">
        <div className="space-y-2">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="text-label-large font-semibold">{title}</div>
            <div className="flex items-center gap-x-2 text-label-extra-small">
              {helperTextOptions?.map((option, index) => (
                <div
                  className={cn(
                    "text-xs font-normal text-text-secondary",
                    index < helperTextOptions?.length - 1
                      ? "border-r border-neutral-600 pr-2"
                      : "",
                  )}
                  key={`helper-text-option-${option}`}
                >
                  {option}
                </div>
              ))}
            </div>
          </div>
          {(selectedCriteria?.length ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-1">
              {selectedCriteria?.map((criterion) => (
                <FilterChip
                  filterValue={criterion}
                  key={`selected-criterion-${criterion.criteria}-${criterion.name}-${criterion.operator}`}
                  onRemove={onRemoveCriterion}
                />
              ))}
            </div>
          ) : null}
        </div>
      </V2DisclosureHeader>
      <V2DisclosurePanel>
        <div className="p-md">{children}</div>
      </V2DisclosurePanel>
    </V2Disclosure>
  );
}
